import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter } from 'react-router-dom'
import { PublicClientApplication, EventType, EventMessage, AuthenticationResult } from '@azure/msal-browser';
import { msalConfig } from './authConfig';
import { MsalProvider } from '@azure/msal-react';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { createBrowserHistory } from 'history';
import { AppInsightsErrorBoundary } from "@microsoft/applicationinsights-react-js";
import { AppInsightsContext } from "@microsoft/applicationinsights-react-js";
import './i18n/i18n'; // Import i18n configuration
import { initDevErrorOverlay } from './utils/devErrorOverlay';
import { ThemeModeProvider } from './ThemeContext';

// Initialize Microsoft Clarity
const clarityProjectId = import.meta.env.VITE_CLARITY_PROJECT_ID;
if (clarityProjectId) {
  (function(c: Window & typeof globalThis, l: Document, a: string, r: string, i: string) {
    (c as any)[a] = (c as any)[a] || function(...args: any[]) { ((c as any)[a].q = (c as any)[a].q || []).push(args) };
    const t = l.createElement(r) as HTMLScriptElement; t.async = true; t.src = "https://www.clarity.ms/tag/" + i;
    const y = l.getElementsByTagName(r)[0]; y.parentNode?.insertBefore(t, y);
  })(window, document, "clarity", "script", clarityProjectId);
}

// Initialize dev error overlay for runtime errors
initDevErrorOverlay();

const browserHistory = createBrowserHistory({});
const reactPlugin = new ReactPlugin();

// Initialize Application Insights only when not in dev mode, and only when
// VITE_INSIGHTS_CONNECTION_STRING is configured.
if (!import.meta.env.DEV && import.meta.env.VITE_INSIGHTS_CONNECTION_STRING) {
  const appInsights = new ApplicationInsights({
    config: {
      connectionString: import.meta.env.VITE_INSIGHTS_CONNECTION_STRING,
      extensions: [reactPlugin],
      extensionConfig: {
        [reactPlugin.identifier]: { history: browserHistory }
      },
      enableAutoRouteTracking: true, // Automatically track route changes
      enableUnhandledPromiseRejectionTracking: true, // Track unhandled promise rejections
      autoExceptionInstrumented: true, // Automatically track unhandled exceptions
    }
  });
  appInsights.loadAppInsights();

  appInsights.addTelemetryInitializer((envelope) => {
    if (envelope.data && envelope.data.baseType === 'ExceptionData') {
      const exception = envelope.data.baseData.exceptions[0];

      // Filter out errors that contain "clarity.ms"
      if (exception.message.includes('clarity.ms')) {
        // Returning false will discard the telemetry item
        return false;
      }
    }
    // Allow the telemetry item to be sent
    return true;
  });
}

/**
 * MSAL should be instantiated outside of the component tree to prevent it from being re-instantiated on re-renders.
 * For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
const msalInstance = new PublicClientApplication(msalConfig);

// // Default to using the first account if no account is active on page load
// if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
//   // Account selection logic is app dependent. Adjust as needed for different use cases.
//   msalInstance.setActiveAccount(msalInstance.getActiveAccount()![0]);
// }

const activeAccount = msalInstance.getActiveAccount();
if (activeAccount && msalInstance.getAllAccounts().length > 0) {
  // Account selection logic is app dependent. Adjust as needed for different use cases.
  msalInstance.setActiveAccount(activeAccount);
}


// // Listen for sign-in event and set active account
msalInstance.addEventCallback((event: EventMessage) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
    const payload = event.payload as AuthenticationResult;
    const account = payload.account;
    msalInstance.setActiveAccount(account);
  }
});

async function enableMocking() {
  const useMockServer = import.meta.env.VITE_USE_MOCK_SERVER === 'true';
  if (!useMockServer) {
    // Only use mocks in development
    return;
  }

  const { worker } = await import('./mocks/browser')
  return worker.start()
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

enableMocking().then(() => {
  root.render(
    <MsalProvider instance={msalInstance}>
      <BrowserRouter>
        <ThemeModeProvider>
          <AppInsightsErrorBoundary onError={() => <></>} appInsights={reactPlugin}>
            <AppInsightsContext.Provider value={reactPlugin}>
              <App />
            </AppInsightsContext.Provider>
          </AppInsightsErrorBoundary>
        </ThemeModeProvider>
      </BrowserRouter>
    </MsalProvider>
  );
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
